import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Collapse,
  Typography,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  SupervisorAccount as SupervisorIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { getPaginatedUsers, bulkAssignTherapistsToSupervisors } from '../../../services/api';
import AddTherapistModal from './AddTherapistModal';
import AssignClientsModal from './AssignClientsModal';
import EditTherapistModal from './EditTherapistModal';

function TherapistsTab({ token, stats, onRefresh, defaultMaxClients = 20 }) {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [assignClientsModal, setAssignClientsModal] = useState({ open: false, therapist: null });
  const [editModal, setEditModal] = useState({ open: false, therapist: null });

  useEffect(() => {
    loadTherapists();
  }, [page, rowsPerPage, searchQuery, statusFilter, token]);

  const loadTherapists = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        role: 'therapist',
        sortBy: 'name',
        sortOrder: 'asc'
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const data = await getPaginatedUsers(params, token);
      setTherapists(data.users || []);
      setTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading therapists:', err);
      setError(err.message || 'Failed to load therapists');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const toggleRowExpansion = (therapistId) => {
    setExpandedRows(prev => ({
      ...prev,
      [therapistId]: !prev[therapistId]
    }));
  };

  const handleAddSuccess = () => {
    loadTherapists();
    onRefresh();
    setAddModalOpen(false);
  };

  const handleAssignClientsSuccess = () => {
    loadTherapists();
    onRefresh();
    setAssignClientsModal({ open: false, therapist: null });
  };

  const handleEditSuccess = () => {
    loadTherapists();
    onRefresh();
    setEditModal({ open: false, therapist: null });
  };

  const handleAssignSupervisors = async (therapistId, selectedSupervisorIds) => {
    try {
      await bulkAssignTherapistsToSupervisors([therapistId], selectedSupervisorIds, token);
      loadTherapists();
      onRefresh();
    } catch (err) {
      setError(err.message || 'Failed to assign supervisors');
    }
  };

  const getCapacityColor = (clientCount, maxCapacity) => {
    const utilization = clientCount / maxCapacity;
    if (utilization >= 1.0) return 'error';
    if (utilization >= 0.8) return 'warning';
    return 'success';
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search therapists"
          placeholder="Name or email..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Suspended">Suspended</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
        >
          Add Therapist
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Specializations</TableCell>
              <TableCell>Clients</TableCell>
              <TableCell>Supervisors</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : therapists.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery || statusFilter ? 'No therapists found matching filters' : 'No therapists yet'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              therapists.map((therapist) => (
                <React.Fragment key={therapist._id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRowExpansion(therapist._id)}>
                        {expandedRows[therapist._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{therapist.name}</TableCell>
                    <TableCell>{therapist.email}</TableCell>
                    <TableCell>
                      {therapist.therapistInfo?.specializations?.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {therapist.therapistInfo.specializations.slice(0, 2).map((spec, idx) => (
                            <Chip key={idx} label={spec} size="small" />
                          ))}
                          {therapist.therapistInfo.specializations.length > 2 && (
                            <Chip
                              label={`+${therapist.therapistInfo.specializations.length - 2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">None</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${therapist.therapistInfo?.assignedClientCount || 0}/${therapist.therapistInfo?.maxClientCapacity || 20}`}
                        size="small"
                        color={getCapacityColor(
                          therapist.therapistInfo?.assignedClientCount || 0,
                          therapist.therapistInfo?.maxClientCapacity || 20
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      {therapist.therapistInfo?.assignedSupervisors?.length || 0}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={therapist.status}
                        size="small"
                        color={therapist.status === 'Active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => setEditModal({ open: true, therapist })}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<GroupIcon />}
                          onClick={() => setAssignClientsModal({ open: true, therapist })}
                        >
                          Clients
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRows[therapist._id] ? undefined : 0 }}>
                      <Collapse in={expandedRows[therapist._id]} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Therapist Details
                          </Typography>

                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Auto-Assignment</Typography>
                              <Typography variant="body2">
                                {therapist.therapistInfo?.canReceiveAutoAssignments ? 'Enabled' : 'Disabled'}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">Capacity Utilization</Typography>
                              <Typography variant="body2">
                                {Math.round(((therapist.therapistInfo?.assignedClientCount || 0) / (therapist.therapistInfo?.maxClientCapacity || 20)) * 100)}%
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">Last Assignment</Typography>
                              <Typography variant="body2">
                                {therapist.therapistInfo?.lastClientAssignmentDate
                                  ? new Date(therapist.therapistInfo.lastClientAssignmentDate).toLocaleDateString()
                                  : 'Never'}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">Assigned Supervisors</Typography>
                              {therapist.therapistInfo?.assignedSupervisors?.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {therapist.therapistInfo.assignedSupervisors.map((supervisor) => (
                                    <Chip
                                      key={supervisor._id || supervisor}
                                      label={supervisor.name || supervisor}
                                      size="small"
                                      icon={<SupervisorIcon />}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">None assigned</Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      {/* Add Therapist Modal */}
      <AddTherapistModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={handleAddSuccess}
        token={token}
        defaultMaxClients={defaultMaxClients}
      />

      {/* Assign Clients Modal */}
      {assignClientsModal.therapist && (
        <AssignClientsModal
          open={assignClientsModal.open}
          therapist={assignClientsModal.therapist}
          onClose={() => setAssignClientsModal({ open: false, therapist: null })}
          onSuccess={handleAssignClientsSuccess}
          token={token}
        />
      )}

      {/* Edit Therapist Modal */}
      {editModal.therapist && (
        <EditTherapistModal
          open={editModal.open}
          therapist={editModal.therapist}
          onClose={() => setEditModal({ open: false, therapist: null })}
          onSuccess={handleEditSuccess}
          token={token}
        />
      )}
    </Box>
  );
}

export default TherapistsTab;
